import React, { useState, useEffect, useCallback } from 'react';
import {
    Table, Card, Input, Select, Button, Space, Typography,
    message, Dropdown, Modal, Tag
} from 'antd';
import {
    PlusOutlined, EditOutlined,
    DeleteOutlined, MoreOutlined, TeamOutlined, DownloadOutlined
} from '@ant-design/icons';
import { userService } from '../services/api';
import UserDrawer from '../components/UserDrawer';

const { Title, Text } = Typography;
const { Option } = Select;

const PRIMARY_RED = '#DA291C';

const UsersPage = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [roles, setRoles] = useState([]);
    const [filters, setFilters] = useState({
        role_id: null,
        portal: null,
        search: ''
    });

    const [drawer, setDrawer] = useState({
        visible: false,
        mode: 'create',
        userData: null
    });

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const response = await userService.list(filters);
            if (response.status === 'success') {
                setData(response.data);
            }
        } catch (error) {
            message.error('Error al cargar usuarios');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const fetchRoles = async () => {
        try {
            const res = await userService.getRoles();
            if (res.status === 'success') {
                setRoles(res.data);
            }
        } catch (error) {
            console.error('Error fetching roles', error);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        fetchRoles();
    }, []);

    const handleSearch = (value) => {
        setFilters(prev => ({ ...prev, search: value }));
    };

    const handleRoleChange = (value) => {
        setFilters(prev => ({ ...prev, role_id: value }));
    };

    const handlePortalChange = (value) => {
        setFilters(prev => ({ ...prev, portal: value, role_id: null }));
    };

    const openCreateDrawer = () => {
        setDrawer({ visible: true, mode: 'create', userData: null });
    };

    const openEditDrawer = (record) => {
        setDrawer({ visible: true, mode: 'edit', userData: record });
    };

    const handleDelete = (record) => {
        Modal.confirm({
            title: '¿Eliminar usuario?',
            content: (
                <div>
                    <p>¿Estás seguro que deseas eliminar a <strong>{record.name}</strong>? <br></br>
                        <strong>Tener en cuenta:</strong> <br></br>
                        <ul style={{ listStyleType: 'disc' }}>
                            <li>Esta acción inhabilitará la licencia de forma permanente.</li>
                            <li>El usuario perderá el acceso al sistema y a su historial.</li>
                            <li>En caso de reingreso, se deberá gestionar una licencia nueva.</li>
                        </ul>
                        <span>Los registros históricos de actividad se conservarán para fines de auditoría y podrán ser consultados únicamente por el administrador.</span>
                    </p>
                </div>
            ),
            okText: 'Sí, Eliminar',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    const res = await userService.delete(record.uuid);
                    if (res.status === 'success') {
                        message.success('Usuario eliminado');
                        fetchUsers();
                    }
                } catch (error) {
                    message.error('Error al eliminar usuario');
                }
            }
        });
    };

    const handleExport = () => {
        const url = userService.getUsersExportUrl(filters);
        window.open(url, '_blank');
    };

    const columns = [
        {
            title: 'Nombre',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Usuario',
            dataIndex: 'username',
            key: 'username',
            render: (text) => <Tag color="blue">{text}</Tag>
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Extensión',
            dataIndex: 'sip_extension',
            key: 'sip_extension',
            render: (text) => {
                if (!text) return '-';
                try {
                    const sipData = JSON.parse(text);
                    return sipData.extension || '-';
                } catch (e) {
                    return text;
                }
            }
        },
        {
            title: 'Rol',
            dataIndex: 'role_name',
            key: 'role_name',
            render: (text) => <Tag color="purple">{text}</Tag>
        },
        {
            title: 'Portal',
            dataIndex: 'portal',
            key: 'portal',
            render: (text) => (
                <Tag color={text === 'admin' ? 'orange' : 'cyan'}>
                    {text === 'admin' ? 'ADMIN' : 'AGENTE'}
                </Tag>
            )
        },
        {
            title: 'Último acceso',
            dataIndex: 'last_login',
            key: 'last_login',
            render: (text) => text ? new Date(text).toLocaleString() : <Text type="secondary" italic>Nunca</Text>
        },
        {
            title: '',
            key: 'actions',
            width: 80,
            render: (_, record) => {
                const items = [
                    {
                        key: 'edit',
                        label: 'Editar',
                        icon: <EditOutlined />,
                        onClick: () => openEditDrawer(record)
                    },
                    {
                        key: 'delete',
                        label: 'Eliminar',
                        icon: <DeleteOutlined />,
                        danger: true,
                        onClick: () => handleDelete(record)
                    }
                ];

                return (
                    <Dropdown menu={{ items }} trigger={['click']}>
                        <Button type="text" icon={<MoreOutlined />} />
                    </Dropdown>
                );
            }
        }
    ];

    return (
        <Space direction="vertical" size="large" style={{ display: 'flex', width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={2} style={{ margin: 0 }}>Gestión de Usuarios</Title>
                <Space>
                    <Button
                        icon={<DownloadOutlined />}
                        size="large"
                        onClick={handleExport}
                        disabled={data.length === 0}
                    >
                        Exportar
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        size="large"
                        style={{ background: PRIMARY_RED, borderColor: PRIMARY_RED }}
                        onClick={openCreateDrawer}
                    >
                        Nuevo Usuario
                    </Button>
                </Space>
            </div>

            <Card bodyStyle={{ padding: '16px' }}>
                <Space wrap size="middle">
                    <Input.Search
                        placeholder="Buscar por nombre o usuario..."
                        onSearch={handleSearch}
                        style={{ width: 300 }}
                        allowClear
                    />
                    <Select
                        placeholder="Filtrar por Portal"
                        style={{ width: 160 }}
                        onChange={handlePortalChange}
                        allowClear
                    >
                        <Option value={null}>Ambos Portales</Option>
                        <Option value="admin">Administrador</Option>
                        <Option value="agent">Agente</Option>
                    </Select>
                    <Select
                        placeholder="Filtrar por Rol"
                        style={{ width: 220 }}
                        onChange={handleRoleChange}
                        value={filters.role_id}
                        allowClear
                        showSearch
                        optionFilterProp="children"
                    >
                        <Option value={null}>Todos los roles</Option>
                        {roles
                            .filter(r => !filters.portal || r.portal === filters.portal)
                            .map(r => (
                                <Option key={r.id} value={r.id}>
                                    {r.name}
                                </Option>
                            ))}
                    </Select>
                </Space>
            </Card>

            <Table
                columns={columns}
                dataSource={data}
                rowKey="uuid"
                loading={loading}
                pagination={{
                    showTotal: (total) => `${total} usuarios registrados`,
                    pageSize: 10
                }}
            />

            <UserDrawer
                visible={drawer.visible}
                mode={drawer.mode}
                userData={drawer.userData}
                onClose={() => setDrawer({ ...drawer, visible: false })}
                onSuccess={fetchUsers}
            />
        </Space>
    );
};

export default UsersPage;
